import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home as HomeIcon, Ruler, Calendar, Check } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import ContactForm from '../components/ContactForm';
import { useLang } from '../context/LanguageContext';
import { fetchProperty, trackPropertyView } from '../lib/api';
import { pickTranslated, localizePrice } from '../lib/propertyI18n';

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setProperty(null);
    fetchProperty(slug)
      .then((data) => {
        setProperty(data);
        trackPropertyView(slug);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="px-6 py-32 text-center">
        <p className="text-neutral-500">{t.listing.not_found}</p>
        <button onClick={() => navigate('/new-projects')} className="mt-6 inline-flex items-center gap-2 text-sm hover:text-[#e11f3a]">
          <ArrowLeft className="w-4 h-4" /> {t.listing.back}
        </button>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="px-6 py-32">
        <div className="max-w-[1200px] mx-auto animate-pulse">
          <div className="h-8 w-1/3 bg-neutral-100 mb-6" />
          <div className="aspect-[16/9] bg-neutral-100" />
        </div>
      </div>
    );
  }

  // Build a de-duplicated gallery: hero first, then any images not equal to hero.
  const rawImages = property.images && property.images.length ? property.images : [];
  const gallery = [property.hero, ...rawImages.filter((img) => img !== property.hero)];
  const images = gallery.length ? gallery : [property.hero];

  return (
    <div>
      <div className="px-6 md:px-12 lg:px-16 pt-8">
        <Link to="/new-projects" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#e11f3a]">
          <ArrowLeft className="w-4 h-4" /> {t.listing.back}
        </Link>
      </div>

      <section className="px-6 md:px-12 lg:px-16 py-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 relative aspect-[16/10] overflow-hidden bg-neutral-100">
              <img src={images[activeImage]} alt={property.title} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
              {images.slice(1, 4).map((img, i) => {
                const idx = i + 1;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`relative aspect-[4/3] overflow-hidden bg-neutral-100 border-2 transition-all ${activeImage === idx ? 'border-[#e11f3a]' : 'border-transparent'}`}
                  >
                    <img src={img} alt={`${property.title} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <p className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-500"><MapPin className="w-3.5 h-3.5" />{pickTranslated(property, lang, 'location')}</p>
              <h1 className="mt-4 text-3xl md:text-5xl font-light uppercase tracking-[0.15em]">{pickTranslated(property, lang, 'title')}</h1>
              {property.price && <p className="mt-4 text-[13px] tracking-[0.25em] uppercase text-[#e11f3a]">{localizePrice(property.price, t)}</p>}
              <p className="mt-8 text-[15px] leading-relaxed text-neutral-700 whitespace-pre-line">{pickTranslated(property, lang, 'description')}</p>

              {(() => {
                const features = pickTranslated(property, lang, 'features') || [];
                if (!features.length) return null;
                return (
                  <div className="mt-10">
                    <SectionLabel>{t.listing.features.toUpperCase()}</SectionLabel>
                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                      {features.map((f) => (
                        <li key={f} className="flex items-center gap-3 text-sm text-neutral-700">
                          <span className="w-6 h-6 border border-[#e11f3a] flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-[#e11f3a]" />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>

            <aside className="bg-neutral-50 p-8 h-fit sticky top-24">
              <h3 className="text-[13px] tracking-[0.25em] text-neutral-500">{t.listing.details}</h3>
              <dl className="mt-6 space-y-4 text-sm">
                {property.surface && (
                  <div className="flex justify-between gap-4">
                    <dt className="flex items-center gap-2 text-neutral-500"><Ruler className="w-4 h-4" />{t.listing.surface}</dt>
                    <dd className="font-medium">{property.surface}</dd>
                  </div>
                )}
                {property.units && (
                  <div className="flex justify-between gap-4">
                    <dt className="flex items-center gap-2 text-neutral-500"><HomeIcon className="w-4 h-4" />{t.listing.units}</dt>
                    <dd className="font-medium">{property.units}</dd>
                  </div>
                )}
                {property.year && (
                  <div className="flex justify-between gap-4">
                    <dt className="flex items-center gap-2 text-neutral-500"><Calendar className="w-4 h-4" />{t.listing.year}</dt>
                    <dd className="font-medium">{property.year}</dd>
                  </div>
                )}
                {property.architect && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">{t.listing.architect}</dt>
                    <dd className="font-medium text-right">{property.architect}</dd>
                  </div>
                )}
              </dl>
              <a href="#request" className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-black text-white px-6 py-3 text-sm tracking-[0.15em] hover:bg-[#e11f3a] transition-colors">
                {t.listing.request_info}
              </a>
            </aside>
          </div>

          <div id="request" className="mt-24 grid lg:grid-cols-2 gap-16">
            <div>
              <SectionLabel>{t.sections.contact_us}</SectionLabel>
              <p className="mt-6 text-neutral-700">{t.sections.contact_intro}</p>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
